// GTM / dataLayer event emitter for the popup widget.
//
// The popup renders inside a Shadow DOM for CSS isolation, which means
// GTM's built-in click / element-visibility triggers cannot see into it.
// Pushing named events to `window.dataLayer` is the cleanest integration
// path for clients: it is unaffected by shadow boundaries and provides a
// stable, documented contract for GTM triggers.

export type PopupGtmEventName =
    | "flowapt_popup_loaded"
    | "flowapt_popup_shown"
    | "flowapt_popup_closed"
    | "flowapt_popup_submitted"
    | "flowapt_popup_cta_click"
    | "flowapt_popup_error"
    | "flowapt_popup_teaser_shown"
    | "flowapt_popup_teaser_click";

export interface PopupGtmEventPayload {
    organization_id: string;
    popup_id: string;
    // Optional context, added by specific emitters below
    [key: string]: unknown;
}

declare global {
    interface Window {
        dataLayer?: Array<Record<string, unknown>>;
    }
}

function pushEvent(event: PopupGtmEventName, payload: PopupGtmEventPayload): void {
    if (typeof window === "undefined") {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event,
        ...payload,
    });
}

interface BaseContext {
    organizationId: string;
    // Optional: the active popup is resolved from the organisation at runtime, so an
    // id may not exist yet (e.g. a settings_failed error before the popup is resolved).
    popupId?: string;
    debug?: boolean;
}

function baseFields(ctx: BaseContext): PopupGtmEventPayload {
    return {
        organization_id: ctx.organizationId,
        popup_id: ctx.popupId ?? "",
    };
}

function log(ctx: BaseContext, event: PopupGtmEventName, extra?: Record<string, unknown>): void {
    if (ctx.debug) {
        // eslint-disable-next-line no-console
        console.log(`[flowapt-popup] dataLayer.push`, { event, ...baseFields(ctx), ...extra });
    }
}

export function emitPopupLoaded(ctx: BaseContext): void {
    pushEvent("flowapt_popup_loaded", baseFields(ctx));
    log(ctx, "flowapt_popup_loaded");
}

export function emitPopupShown(ctx: BaseContext, extra?: { trigger_type?: string }): void {
    const payload = { ...baseFields(ctx), ...(extra || {}) };
    pushEvent("flowapt_popup_shown", payload);
    log(ctx, "flowapt_popup_shown", extra);
}

export function emitPopupClosed(ctx: BaseContext, extra?: { step?: string }): void {
    const payload = { ...baseFields(ctx), ...(extra || {}) };
    pushEvent("flowapt_popup_closed", payload);
    log(ctx, "flowapt_popup_closed", extra);
}

export function emitPopupSubmitted(
    ctx: BaseContext,
    extra?: { fields_submitted?: string[] }
): void {
    // NOTE: we intentionally only send the list of field *names* that were
    // filled in, never the values. Clients wire their own PII handling in GTM.
    const payload = { ...baseFields(ctx), ...(extra || {}) };
    pushEvent("flowapt_popup_submitted", payload);
    log(ctx, "flowapt_popup_submitted", extra);
}

export function emitPopupCtaClick(ctx: BaseContext, extra?: { step?: string }): void {
    const payload = { ...baseFields(ctx), ...(extra || {}) };
    pushEvent("flowapt_popup_cta_click", payload);
    log(ctx, "flowapt_popup_cta_click", extra);
}

export function emitPopupError(
    ctx: BaseContext,
    extra: { error_type: "submit_failed" | "settings_failed"; message?: string }
): void {
    const payload = { ...baseFields(ctx), ...extra };
    pushEvent("flowapt_popup_error", payload);
    log(ctx, "flowapt_popup_error", extra);
}

export function emitPopupTeaserShown(
    ctx: BaseContext,
    extra?: { mode?: string }
): void {
    const payload = { ...baseFields(ctx), ...(extra || {}) };
    pushEvent("flowapt_popup_teaser_shown", payload);
    log(ctx, "flowapt_popup_teaser_shown", extra);
}

export function emitPopupTeaserClick(ctx: BaseContext): void {
    pushEvent("flowapt_popup_teaser_click", baseFields(ctx));
    log(ctx, "flowapt_popup_teaser_click");
}
